// Composable for market price validation logic (no labels, no translations)
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'

import { PRICE_IS_TO_LOW_RULE } from '~/scopes/listings/pages/index/components/Listing/components/MarketList/MarketList.constants'

export function useMarketPriceValidation() {
  /**
   * Returns true if the target price is too high compared to the minimum price (more than 8% of the minimum price).
   */
  function isTargetPriceTooHigh(
    market: MarketCountryCode,
    minimumPricesValue: Partial<Record<MarketCountryCode, number | undefined>>,
    targetPricesValue: Partial<Record<MarketCountryCode, number | undefined>>,
  ): boolean {
    const minimumPrice = Number(minimumPricesValue[market])
    const targetPrice = Number(targetPricesValue[market])

    if (!minimumPrice || !targetPrice) {
      return false
    }

    const maximumTargetPrice =
      minimumPrice * PRICE_IS_TO_LOW_RULE.MAXIMUM_TARGET_PRICE_MULTIPLIER

    return targetPrice > maximumTargetPrice
  }

  /**
   * Returns true if the minimum price is above the target price.
   */
  function isMinimumPriceAboveTargetPrice(
    market: MarketCountryCode,
    minimumPricesValue: Partial<Record<MarketCountryCode, number | undefined>>,
    targetPricesValue: Partial<Record<MarketCountryCode, number | undefined>>,
  ): boolean {
    const minPrice = Number(minimumPricesValue[market])
    const targetPrice = Number(targetPricesValue[market])

    if (!minPrice || !targetPrice) return false

    return minPrice > targetPrice
  }

  /**
   * Returns true if the target price exceeds the price when sold new.
   */
  function isTargetPriceAbovePriceWhenSoldNew(
    marketCountryCode: MarketCountryCode,
    targetPricesValue: Partial<Record<MarketCountryCode, number | undefined>>,
    priceWhenSoldNew: number | undefined,
  ): boolean {
    const targetPrice = Number(targetPricesValue[marketCountryCode])
    const newPrice = Number(priceWhenSoldNew)
    if (!targetPrice || !newPrice) return false

    return targetPrice > newPrice
  }

  return {
    isTargetPriceTooHigh,
    isMinimumPriceAboveTargetPrice,
    isTargetPriceAbovePriceWhenSoldNew,
  }
}
